import React, { useState, useEffect, useMemo, useRef } from 'react'
import { useQuery, useMutation, useClient } from 'urql'
import { GET_USERS } from '../../graphql/users'
import { GET_COMPANIES } from '../../graphql/companies'
import {
  BULK_UPDATE_USER_BLOCKED_FEATURES,
  BULK_UPDATE_USER_PERMISSIONS,
} from '../../graphql/users'
import {
  BULK_UPDATE_COMPANY_BLOCKED_FEATURES,
  BULK_UPDATE_COMPANY_PERMISSIONS,
} from '../../graphql/companies'
import './dashboard.css'
import { resolveModuleHasAccess } from '../../utils/resolveModuleHasAccess'
import { FICHA_CLIENTE_MODULE_ID } from '../../constants/fichaClienteModule'
import { USER_ONLY_MODULE_IDS } from '../../constants/configuracionEquipoModule'
import { isRepositorioParentModuleId } from '../../constants/repositorioParentModule'

import { EXPORT_BLOCKABLE_FEATURES } from '../../constants/exportBlockableFeatures'

// Funcionalidades que se pueden bloquear
const BLOCKABLE_FEATURES = [
  { id: 'conciliacion', label: 'Conciliación', description: 'Conciliación Bancaria y botones Conciliar' },
  { id: 'nomina', label: 'Nómina', description: 'Generación de Nómina de Pagos' },
  { id: 'asiento', label: 'Asiento', description: 'Botones Nuevo Asiento y Crear Asiento' },
  { id: 'f29', label: 'F29', description: 'Declarar automáticamente y Enviar por Email en F29' },
  { id: 'pascale_chat', label: 'Chat Pascale', description: 'Asistente Pascale en el portal (icono morado). Por defecto bloqueado; quitar el bloqueo en usuario y/o empresa para habilitar' },
  { id: 'crear_obligacion', label: 'Crear Obligación', description: 'Crear e importar obligaciones en el portal (no reglas automáticas)' },
  { id: 'abonar', label: 'Abonar', description: 'Abonar documentos y saldos con métodos de pago' },
  {
    id: 'cargar_plan_cuenta',
    label: 'Cargar Plan de Cuenta',
    optIn: true,
    description: 'Importar plan de cuentas desde Excel (Activar = añade permiso al array)',
  },
  {
    id: 'cargar_repositorio',
    label: 'Cargar en Repositorio',
    optIn: true,
    description: 'Subir archivos en el Repositorio (ADMIN/SUPER_COLABORADOR por defecto)',
  },
  {
    id: 'notificar_repositorio',
    label: 'Notificar en Repositorio',
    optIn: true,
    description: 'Notificar documentos del Repositorio por email (ADMIN/SUPER_COLABORADOR por defecto)',
  },
  {
    id: 'editar_saldos_flujo_caja',
    label: 'Editar saldos de Flujo de Caja',
    optIn: true,
    userOnly: true,
    description: 'Editar saldos existentes en Flujo de Caja (ADMIN/SUPER_COLABORADOR/COLABORADOR por defecto). Agregar saldo no se restringe. Solo usuarios.',
  },
  ...EXPORT_BLOCKABLE_FEATURES.map((f) => ({
    id: f.id,
    label: f.label.replace(/^Bloquear exportación /, 'Export ').replace(/^Bloquear API: /, 'API '),
    optIn: !!f.optIn,
    description: f.description,
  })),
]

// Estructura de módulos (idéntica a UserDetail y CompanyDetail)
const MODULE_CATEGORIES = [
  { id: 'dashboard', name: 'Dashboard', subModules: [] },
  {
    id: 'onboarding',
    name: 'Onboarding',
    subModules: [{ id: 'softlanding', name: 'Configuración Inicial' }],
  },
  {
    id: 'reportes', name: 'Reportes', subModules: [
      { id: 'reportes_ventas', name: 'Reporte de Ventas' },
      { id: 'reportes_compras', name: 'Reporte de Compras' },
      { id: 'reportes_cobranza', name: 'Reporte de Cobranza' },
      { id: 'reportes_proveedores', name: 'Reporte de Proveedores' },
      { id: 'reportes_dj', name: 'Reporte DDJJ' },
      { id: 'reportes_rrhh', name: 'Reporte RRHH' },
      { id: 'reportes_personalizados', name: 'Personalizados' },
    ]
  },
  {
    id: 'finanzas', name: 'Finanzas', subModules: [
      { id: 'flujo_caja', name: 'Flujo de Caja' },
      { id: 'eerr', name: 'Estado de Resultados de Gestión' },
      { id: 'eoaf', name: 'EOAF' },
      { id: 'creditos', name: 'Préstamos' },
      { id: 'estructuras_maestras', name: 'Estructuras Maestras (configuración)' },
      { id: 'estado_de_resultados_gestion', name: 'EERR por Estructura Maestra' },
      { id: 'balance_clasificado_gestion', name: 'Balance Clasificado por Estructura Maestra' },
    ]
  },
  {
    id: 'documentos', name: 'Documentos', subModules: [
      { id: 'documentos_tributarios', name: 'DTEs' },
      { id: 'boletas', name: 'Boletas de Honorarios' },
      { id: 'obligaciones', name: 'Obligaciones' },
      { id: 'cuotas_creditos', name: 'Cuotas de Créditos' },
      { id: 'din', name: 'Declaraciones de Ingreso' },
    ]
  },
  {
    id: 'operaciones', name: 'Operaciones', subModules: [
      { id: 'conciliacion', name: 'Conciliación Bancaria' },
      { id: 'cobranza', name: 'Cobranza' },
      { id: 'pago_proveedores', name: 'Pago a Proveedores' },
      { id: 'cuadre_cartolas', name: 'Cuadre de Cartolas' },
      { id: 'tarjetas_credito', name: 'Tarjetas de Crédito' },
    ]
  },
  {
    id: 'impuestos', name: 'Impuestos', subModules: [
      { id: 'formulario_29', name: 'Formulario 29' },
      { id: 'consolidado_f29', name: 'Consolidado F29' },
      { id: 'multas_sii', name: 'Multas en el SII' },
      { id: 'cuadre_formulario_29', name: 'Cuadre Formulario 29' },
    ]
  },
  {
    id: 'contabilidad', name: 'Contabilidad', subModules: [
      { id: 'balance_8_columnas', name: 'Balance 8 Columnas' },
      { id: 'balance_clasificado', name: 'Balance Clasificado' },
      { id: 'eerr_contable', name: 'Estado de Resultados Contable' },
      { id: 'libro_mayor', name: 'Libro Mayor' },
      { id: 'libro_diario', name: 'Libro Diario' },
      { id: 'asientos', name: 'Asientos Contables' },
      { id: 'plan_cuentas', name: 'Plan de Cuentas' },
      { id: 'centralizacion_contable', name: 'Centralización Contable' },
      { id: 'libro_compra_venta', name: 'Libro Compra/Venta' },
      { id: 'cierre_apertura_contable', name: 'Cierre/Apertura Contable' },
    ]
  },
  {
    id: 'recursos_humanos', name: 'Recursos Humanos', subModules: [
      { id: 'haberes_variables', name: 'Haberes Variables' },
      { id: 'cargar_nuevo_empleado', name: 'Cargar Nuevo Empleado' },
      { id: 'contratos_proximos_a_vencer', name: 'Contratos Próximos a Vencer' },
      { id: 'validador_liquidaciones', name: 'Validador de Liquidaciones' },
    ]
  },
  {
    id: 'repositorio', name: 'Repositorio', subModules: [
      { id: 'repositorio_informacion_inicial', name: 'Información Inicial' },
      { id: 'repositorio_informes_financieros', name: 'Informes Contables anuales' },
      { id: 'repositorio_presupuestos', name: 'Presupuestos' },
      { id: 'repositorio_certificados', name: 'Certificados' },
      { id: 'repositorio_formularios', name: 'Formularios' },
      { id: 'repositorio_libro_remuneraciones', name: 'Libros de Remuneraciones' },
      { id: 'repositorio_haberes_variables', name: 'Haberes Variables' },
      { id: 'repositorio_directorio', name: 'Informes Financieros Mensuales' },
      { id: 'repositorio_auditorias', name: 'Auditorías' },
      { id: 'repositorio_cartola_diaria', name: 'Cartola Diaria' },
      { id: 'repositorio_proveedores_hoja', name: 'Hoja Proveedores' },
      { id: 'repositorio_flujo_caja_mensual', name: 'Flujo de Caja Mensual' },
      { id: 'repositorio_flujo_caja_quincenal', name: 'Flujo de Caja Quincenal' },
      { id: 'repositorio_flujo_caja_semanal', name: 'Flujo de Caja Semanal' },
    ]
  },
  {
    id: 'seguimiento', name: 'Seguimiento', subModules: [
      { id: 'ficha_cliente', name: 'Ficha Cliente' },
      { id: 'avance_contable', name: 'Estado de Avance' },
    ]
  },
  {
    id: 'ajustes', name: 'Ajustes', subModules: [
      { id: 'conexiones', name: 'Conexiones' },
      { id: 'agenda', name: 'Contactos' },
      { id: 'configuracion', name: 'Configuración' },
      { id: 'configuracion_equipo', name: 'Equipo (configuración)' },
      { id: 'configuracion_api_key', name: 'API Key (configuración)' },
      { id: 'configuracion_automatizaciones', name: 'Automatizaciones (configuración)' },
      { id: 'descargas', name: 'Descargas' },
    ]
  },
]

const getAllModuleIds = (entityScope = 'users') => {
  const ids = []
  MODULE_CATEGORIES.forEach(cat => {
    if (!isRepositorioParentModuleId(cat.id)) ids.push(cat.id)
    cat.subModules.forEach(sub => {
      if (entityScope === 'companies' && USER_ONLY_MODULE_IDS.includes(sub.id)) return
      ids.push(sub.id)
    })
  })
  return ids
}

const getModuleCategoriesForScope = (entityScope) => {
  if (entityScope === 'users') return MODULE_CATEGORIES
  return MODULE_CATEGORIES.map((cat) => ({
    ...cat,
    subModules: cat.subModules.filter((sub) => !USER_ONLY_MODULE_IDS.includes(sub.id)),
  }))
}

// ============================================
// Mapa de etiquetas legibles para módulos y funcionalidades
// ============================================
const MODULE_LABELS = (() => {
  const m = {}
  MODULE_CATEGORIES.forEach(cat => {
    m[cat.id] = cat.name
    cat.subModules.forEach(sub => { m[sub.id] = sub.name })
  })
  return m
})()
const FEATURE_LABELS = BLOCKABLE_FEATURES.reduce((acc, f) => {
  acc[f.id] = f.label
  return acc
}, {})

const labelForModule = (id) => MODULE_LABELS[id] || id
const labelForFeature = (id) => FEATURE_LABELS[id] || id

// ============================================
// Utilidades de detección de discrepancias
// ============================================

// Calcula el conflicto de UNA entidad contra la nueva configuración a aplicar.
// Devuelve { hasConflict, conflicts: [{ id, label, current, next }] }
// - "current" y "next" son strings legibles ("Bloqueada" / "Activa")
function computeEntityConflict(entity, permissionType, blockedFeatures, modulePermissions) {
  if (permissionType === 'blocked') {
    const current = entity.blockedFeatures || []
    const conflicts = []

    // Funcionalidades que están bloqueadas hoy y dejarían de estarlo
    current.forEach(featId => {
      if (!blockedFeatures.includes(featId)) {
        conflicts.push({
          id: featId,
          label: labelForFeature(featId),
          current: 'Bloqueada',
          next: 'Activa',
        })
      }
    })
    // Funcionalidades que NO están bloqueadas hoy y se bloquearían
    blockedFeatures.forEach(featId => {
      if (!current.includes(featId)) {
        conflicts.push({
          id: featId,
          label: labelForFeature(featId),
          current: 'Activa',
          next: 'Bloqueada',
        })
      }
    })
    return { hasConflict: conflicts.length > 0, conflicts }
  }

  // permissionType === 'modules'
  const currentPerms = entity.modulePermissions || []
  const conflicts = []
  modulePermissions.forEach(newPerm => {
    const cur = currentPerms.find(p => p.moduleId === newPerm.moduleId)
    const curAccess = resolveModuleHasAccess(
      cur,
      newPerm.moduleId,
      true,
      newPerm.moduleId === FICHA_CLIENTE_MODULE_ID ? entity.roles : undefined,
      entity.modulePermissions,
    )
    if (curAccess !== newPerm.hasAccess) {
      conflicts.push({
        id: newPerm.moduleId,
        label: labelForModule(newPerm.moduleId),
        current: curAccess ? 'Activo' : 'Bloqueado',
        next: newPerm.hasAccess ? 'Activo' : 'Bloqueado',
      })
    }
  })
  return { hasConflict: conflicts.length > 0, conflicts }
}

// Filtra entidades en conflicto
function detectDiscrepancies(entities, permissionType, blockedFeatures, modulePermissions) {
  return entities
    .map(e => ({ entity: e, ...computeEntityConflict(e, permissionType, blockedFeatures, modulePermissions) }))
    .filter(x => x.hasConflict)
}

// ============================================
// Modal de discrepancias (claro y con comparación lado a lado)
// ============================================
function DiscrepancyModal({ isOpen, onClose, onApplyAll, onApplyConflictingOnly, discrepancies, entityType, totalSelected }) {
  if (!isOpen) return null

  const entityLabel = entityType === 'users' ? 'usuario' : 'empresa'
  const entityLabelPlural = entityType === 'users' ? 'usuarios' : 'empresas'
  const conflictCount = discrepancies.length
  const unchangedCount = totalSelected - conflictCount

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="modal-content"
        style={{ maxWidth: '780px', width: '95%', maxHeight: '85vh', display: 'flex', flexDirection: 'column' }}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="modal-header" style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0', padding: '1rem 1.25rem' }}>
          <div>
            <h3 style={{ margin: 0, color: '#1e293b', fontSize: '1.1rem' }}>
              {conflictCount} {conflictCount === 1 ? entityLabel : entityLabelPlural} con configuración distinta
            </h3>
            <p style={{ margin: '0.35rem 0 0', fontSize: '0.85rem', color: '#64748b' }}>
              Tienen permisos distintos a los que estás por aplicar. Revisa qué cambiaría.
              {unchangedCount > 0 && (
                <> Los otros {unchangedCount} seleccionado{unchangedCount !== 1 ? 's' : ''} ya coinciden con esta configuración.</>
              )}
            </p>
          </div>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>

        {/* Body */}
        <div className="modal-body" style={{ overflowY: 'auto', flex: 1, padding: '1rem 1.25rem', background: '#fafafa' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            {discrepancies.map(({ entity, conflicts }) => {
              const name = entityType === 'users'
                ? (entity.email || 'Sin email')
                : (entity.name || 'Sin nombre')
              const sub = entityType === 'users'
                ? [entity.firstName, entity.lastName].filter(Boolean).join(' ')
                : entity.rut || ''
              return (
                <div key={entity._id} style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: '10px', overflow: 'hidden' }}>
                  <div style={{ background: '#f9fafb', padding: '0.6rem 0.9rem', borderBottom: '1px solid #e5e7eb' }}>
                    <div style={{ fontWeight: 600, fontSize: '0.9rem', color: '#111827' }}>{name}</div>
                    {sub && <div style={{ fontSize: '0.78rem', color: '#6b7280' }}>{sub}</div>}
                  </div>
                  <div style={{ padding: '0.5rem 0.9rem' }}>
                    <table style={{ width: '100%', fontSize: '0.82rem', borderCollapse: 'collapse' }}>
                      <thead>
                        <tr style={{ color: '#6b7280', textAlign: 'left' }}>
                          <th style={{ padding: '0.35rem 0.25rem', fontWeight: 500 }}>Permiso</th>
                          <th style={{ padding: '0.35rem 0.25rem', fontWeight: 500, width: '110px' }}>Actual</th>
                          <th style={{ padding: '0.35rem 0.25rem', fontWeight: 500, width: '20px', textAlign: 'center' }}></th>
                          <th style={{ padding: '0.35rem 0.25rem', fontWeight: 500, width: '110px' }}>Quedará</th>
                        </tr>
                      </thead>
                      <tbody>
                        {conflicts.map(c => {
                          const currentIsBad = c.current === 'Bloqueada' || c.current === 'Bloqueado'
                          const nextIsBad = c.next === 'Bloqueada' || c.next === 'Bloqueado'
                          return (
                            <tr key={c.id} style={{ borderTop: '1px solid #f3f4f6' }}>
                              <td style={{ padding: '0.35rem 0.25rem', color: '#1f2937' }}>{c.label}</td>
                              <td style={{ padding: '0.35rem 0.25rem' }}>
                                <span style={{
                                  padding: '2px 8px', borderRadius: '999px', fontSize: '0.72rem', fontWeight: 600,
                                  background: currentIsBad ? '#fee2e2' : '#d1fae5',
                                  color: currentIsBad ? '#991b1b' : '#065f46',
                                }}>{c.current}</span>
                              </td>
                              <td style={{ textAlign: 'center', color: '#9ca3af' }}>→</td>
                              <td style={{ padding: '0.35rem 0.25rem' }}>
                                <span style={{
                                  padding: '2px 8px', borderRadius: '999px', fontSize: '0.72rem', fontWeight: 600,
                                  background: nextIsBad ? '#fee2e2' : '#d1fae5',
                                  color: nextIsBad ? '#991b1b' : '#065f46',
                                }}>{c.next}</span>
                              </td>
                            </tr>
                          )
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )
            })}
          </div>
        </div>

        {/* Footer / Acciones */}
        <div style={{ padding: '0.85rem 1.25rem', borderTop: '1px solid #e5e7eb', background: '#fff' }}>
          <p style={{ margin: '0 0 0.75rem', fontSize: '0.82rem', color: '#374151' }}>
            ¿Qué quieres hacer?
          </p>
          <div style={{ display: 'flex', gap: '0.6rem', justifyContent: 'flex-end', flexWrap: 'wrap' }}>
            <button className="action-btn secondary" onClick={onClose}>
              Cancelar
            </button>
            {conflictCount < totalSelected && (
              <button
                className="action-btn secondary"
                style={{ borderColor: '#488E9D', color: '#2f5f6b' }}
                onClick={onApplyConflictingOnly}
                title="Solo envía la actualización a quienes tienen permisos distintos; el resto ya está alineado y no se modifica."
              >
                Aplicar solo a los {conflictCount} que necesitan cambio
              </button>
            )}
            <button
              className="action-btn primary"
              style={{ background: '#dc2626', borderColor: '#dc2626' }}
              onClick={onApplyAll}
              title="Aplicar a todos los seleccionados, incluidos los que ya tenían la misma configuración (operación redundante en esos casos)."
            >
              Sobreescribir y aplicar a los {totalSelected}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

// ============================================
const BULK_LIST_INITIAL_SIZE = 100
const BULK_LIST_SEARCH_SIZE = 50
const BULK_LIST_MULTI_SEARCH_SIZE = 200
const BULK_SELECT_ALL_PAGE_SIZE = 1000
const BULK_SEARCH_MIN_CHARS = 2
const BULK_SEARCH_DEBOUNCE_MS = 300

function applyEntityClientFilters(items, { isUsers, roleFilter }) {
  return items.filter((item) => {
    if (isUsers && roleFilter === 'USER') {
      const roles = item.roles || []
      const hasPrivileged =
        roles.includes('ADMIN') ||
        roles.includes('SUPER_COLABORADOR') ||
        roles.includes('DPO') ||
        roles.includes('COLABORADOR')
      if (hasPrivileged) return false
    }
    if (!isUsers && roleFilter !== 'ALL') {
      if (item.scfo_plan !== roleFilter) return false
    }
    return true
  })
}

function hasClientOnlyEntityFilter({ isUsers, roleFilter }) {
  return isUsers ? roleFilter === 'USER' : roleFilter !== 'ALL'
}

/** Activa búsqueda en catálogo: texto ≥2 chars o lista separada por ";". */
function shouldTriggerCatalogSearch(term) {
  const raw = term.trim()
  if (!raw) return false
  if (raw.includes(';')) {
    return raw.split(';').some((part) => part.trim().length >= 2)
  }
  return raw.length >= BULK_SEARCH_MIN_CHARS
}

function buildEntityListFilter({ isUsers, debouncedSearch, roleFilter, statusFilter }) {
  const term = debouncedSearch.trim()
  const multiTerm = term.includes(';')
  const catalogSearch = shouldTriggerCatalogSearch(term)
  const filter = {
    page: 1,
    pageSize: catalogSearch
      ? (multiTerm ? BULK_LIST_MULTI_SEARCH_SIZE : BULK_LIST_SEARCH_SIZE)
      : BULK_LIST_INITIAL_SIZE,
  }
  if (catalogSearch) {
    filter.search = term
  }
  if (isUsers) {
    if (statusFilter === 'ACTIVO' || statusFilter === 'DESACTIVADO') {
      filter.adminStatus = statusFilter
    }
    if (roleFilter !== 'ALL' && roleFilter !== 'USER') {
      filter.role = roleFilter
    }
  }
  return filter
}

// Componente de selección de entidades
// ============================================
function EntitySelector({ entityType, selected, onSelectionChange, onItemsLoaded, permissionType, blockedFeatures, modulePermissions, listRefetchEpoch = 0 }) {
  const [search, setSearch] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [roleFilter, setRoleFilter] = useState('ALL')
  const [statusFilter, setStatusFilter] = useState('ALL')
  const [selectingAll, setSelectingAll] = useState(false)
  const entityCacheRef = useRef(new Map())
  const gqlClient = useClient()

  const isUsers = entityType === 'users'

  useEffect(() => {
    entityCacheRef.current.clear()
    setSearch('')
    setDebouncedSearch('')
  }, [entityType])

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), BULK_SEARCH_DEBOUNCE_MS)
    return () => clearTimeout(timer)
  }, [search])

  const listFilter = useMemo(
    () => buildEntityListFilter({ isUsers, debouncedSearch, roleFilter, statusFilter }),
    [isUsers, debouncedSearch, roleFilter, statusFilter],
  )

  const [{ data, fetching }, reexecuteQuery] = useQuery({
    query: isUsers ? GET_USERS : GET_COMPANIES,
    variables: { filter: listFilter },
    requestPolicy: 'cache-and-network',
  })

  // Tras mutaciones masivas la caché de urql sigue con permisos viejos; forzar red para alinear badges y conflictos.
  useEffect(() => {
    if (listRefetchEpoch > 0) {
      reexecuteQuery({ requestPolicy: 'network-only' })
    }
  }, [listRefetchEpoch, reexecuteQuery])

  const pagination = isUsers ? data?.users : data?.companies

  const queryItems = useMemo(() => {
    return pagination?.items || []
  }, [pagination])

  useEffect(() => {
    queryItems.forEach((item) => {
      entityCacheRef.current.set(item._id, item)
    })
  }, [queryItems])

  const items = useMemo(() => {
    const byId = new Map()
    queryItems.forEach((item) => byId.set(item._id, item))
    selected.forEach((id) => {
      if (!byId.has(id) && entityCacheRef.current.has(id)) {
        byId.set(id, entityCacheRef.current.get(id))
      }
    })
    return Array.from(byId.values())
  }, [queryItems, selected])

  // Exponer items al padre (incluye seleccionados fuera de la página actual)
  useEffect(() => {
    if (onItemsLoaded && items.length > 0) {
      onItemsLoaded(items)
    }
  }, [items, onItemsLoaded])

  const totalCount = pagination?.totalCount ?? items.length

  const filtered = useMemo(
    () => applyEntityClientFilters(items, { isUsers, roleFilter }),
    [items, isUsers, roleFilter],
  )

  const clientOnlyFilter = hasClientOnlyEntityFilter({ isUsers, roleFilter })
  const canSelectBeyondVisible = clientOnlyFilter || totalCount > filtered.length

  const toggleItem = (id) => {
    const item = items.find((i) => i._id === id)
    if (item) entityCacheRef.current.set(id, item)
    onSelectionChange(
      selected.includes(id)
        ? selected.filter(s => s !== id)
        : [...selected, id]
    )
  }

  const selectAllVisible = () => onSelectionChange(filtered.map(i => i._id))

  const selectAllMatching = async () => {
    setSelectingAll(true)
    try {
      const query = isUsers ? GET_USERS : GET_COMPANIES
      let page = 1
      let totalPages = 1
      const allFetched = []

      do {
        const filter = { ...listFilter, page, pageSize: BULK_SELECT_ALL_PAGE_SIZE }
        const result = await gqlClient
          .query(query, { filter }, { requestPolicy: 'network-only' })
          .toPromise()
        if (result.error) throw result.error
        const pagination = isUsers ? result.data?.users : result.data?.companies
        allFetched.push(...(pagination?.items || []))
        totalPages = pagination?.totalPages || 1
        page += 1
      } while (page <= totalPages)

      const matching = applyEntityClientFilters(allFetched, { isUsers, roleFilter })
      matching.forEach((item) => entityCacheRef.current.set(item._id, item))
      const ids = matching.map((i) => i._id)
      onSelectionChange(ids)
      if (onItemsLoaded && matching.length > 0) {
        onItemsLoaded(matching)
      }
    } catch (err) {
      console.error('selectAllMatching', err)
      alert(
        `No se pudieron cargar ${isUsers ? 'todos los usuarios' : 'todas las empresas'}: ${
          err?.message || 'error desconocido'
        }`,
      )
    } finally {
      setSelectingAll(false)
    }
  }

  const clearAll = () => onSelectionChange([])

  return (
    <div className="bulk-entity-selector">
      <div className="search-section">
        <input
          type="text"
          placeholder={
            isUsers
              ? 'Buscar por email o RUT (varios RUT: 18640592-7;12.345.678-5)'
              : 'Buscar por nombre o RUT (varios RUT: 18640592-7;12.345.678-5)'
          }
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="search-input"
        />
        <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.5rem' }}>
          <select
            value={roleFilter}
            onChange={e => setRoleFilter(e.target.value)}
            style={{ flex: 1, padding: '0.35rem 0.5rem', borderRadius: '6px', border: '1px solid #d1d5db', fontSize: '0.85rem', background: '#fff', color: '#374151' }}
          >
            {isUsers ? (
              <>
                <option value="ALL">Todos</option>
                <option value="ADMIN">Admin</option>
                <option value="DPO">DPO</option>
                <option value="SUPER_COLABORADOR">Super Colaborador</option>
                <option value="COLABORADOR">Colaborador</option>
                <option value="USER">Usuario</option>
              </>
            ) : (
              <>
                <option value="ALL">Todos los servicios</option>
                <option value="servicio1">Servicio 1</option>
                <option value="servicio2">Servicio 2</option>
                <option value="servicio3">Servicio 3</option>
                <option value="anualizado">Anualizado</option>
              </>
            )}
          </select>
          {isUsers && (
            <select
              value={statusFilter}
              onChange={e => setStatusFilter(e.target.value)}
              style={{ flex: 1, padding: '0.35rem 0.5rem', borderRadius: '6px', border: '1px solid #d1d5db', fontSize: '0.85rem', background: '#fff', color: '#374151' }}
            >
              <option value="ALL">Todos los estados</option>
              <option value="ACTIVO">Activo</option>
              <option value="DESACTIVADO">Desactivado</option>
            </select>
          )}
        </div>
      </div>

      <div className="selection-actions">
        <span className="selection-count">
          {selected.length} seleccionado{selected.length !== 1 ? 's' : ''}
          {' · '}
          {filtered.length} mostrado{filtered.length !== 1 ? 's' : ''}
          {totalCount > filtered.length ? ` (${totalCount} en total)` : ''}
        </span>
        <div className="selection-buttons">
          {canSelectBeyondVisible && (
            <button
              className="text-btn"
              onClick={selectAllMatching}
              disabled={selectingAll || fetching}
              title={
                clientOnlyFilter
                  ? 'Incluye todos los resultados del filtro, no solo los visibles en la lista'
                  : `Seleccionar las ${totalCount} ${isUsers ? 'usuarios' : 'empresas'} que coinciden con búsqueda y filtros`
              }
            >
              {selectingAll
                ? 'Cargando...'
                : clientOnlyFilter
                  ? 'Seleccionar todos los resultados'
                  : `Seleccionar todos (${totalCount})`}
            </button>
          )}
          <button className="text-btn" onClick={selectAllVisible}>
            {canSelectBeyondVisible ? 'Seleccionar visibles' : 'Seleccionar todos'}
          </button>
          <button className="text-btn" onClick={clearAll}>Limpiar</button>
        </div>
      </div>

      <div className="bulk-entity-list">
        {fetching ? (
          <div className="loading-small">Cargando...</div>
        ) : filtered.length === 0 ? (
          <div className="empty-state-small">
            {search.trim().length > 0 && !shouldTriggerCatalogSearch(search)
              ? `Escribe al menos ${BULK_SEARCH_MIN_CHARS} caracteres, o varios RUT separados por ";" (ej. 18640592-7;12345678-9)`
              : `No se encontraron ${isUsers ? 'usuarios' : 'empresas'}`}
          </div>
        ) : (
          filtered.map(item => {
            const id = item._id
            const isSelected = selected.includes(id)
            // Conflicto contextual: solo cuenta si difiere de lo que se va a aplicar AHORA
            const { hasConflict, conflicts } = computeEntityConflict(item, permissionType, blockedFeatures, modulePermissions)
            const conflictTitle = hasConflict
              ? `${conflicts.length} permiso${conflicts.length !== 1 ? 's' : ''} cambiar${conflicts.length !== 1 ? 'án' : 'á'}: ` +
                conflicts.slice(0, 5).map(c => `${c.label} (${c.current}→${c.next})`).join(', ') +
                (conflicts.length > 5 ? '...' : '')
              : ''
            return (
              <label key={id} className={`company-item ${isSelected ? 'selected' : ''}`}>
                <input
                  type="checkbox"
                  checked={isSelected}
                  onChange={() => toggleItem(id)}
                />
                <div className="company-info">
                  {isUsers ? (
                    <>
                      <span className="company-name">{item.email || 'Sin email'}</span>
                      <span className="company-rut">
                        {[item.firstName, item.lastName].filter(Boolean).join(' ') || 'Sin nombre'}
                        {item.roles?.includes('ADMIN') && ' • Admin'}
                        {item.roles?.includes('DPO') && ' • DPO'}
                        {item.roles?.includes('COLABORADOR') && ' • Colaborador'}
                      </span>
                    </>
                  ) : (
                    <>
                      <span className="company-name">{item.name || 'Sin nombre'}</span>
                      <span className="company-rut">{item.rut || 'Sin RUT'}</span>
                    </>
                  )}
                  {hasConflict && isSelected && (
                    <span
                      title={conflictTitle}
                      style={{ fontSize: '0.7rem', background: '#fee2e2', color: '#991b1b', padding: '1px 8px', borderRadius: '999px', marginTop: '3px', display: 'inline-flex', alignItems: 'center', gap: '3px', alignSelf: 'flex-start' }}
                    >
                      ⚠ {conflicts.length} cambio{conflicts.length !== 1 ? 's' : ''} sobreescribir{conflicts.length !== 1 ? 'án' : 'á'}
                    </span>
                  )}
                </div>
                <span className={`status-indicator ${
                  isUsers
                    ? (item.adminStatus !== 'DESACTIVADO' ? 'active' : 'inactive')
                    : (item.adminStatus !== 'BLOQUEADO' ? 'active' : 'inactive')
                }`}>
                  {isUsers
                    ? (item.adminStatus !== 'DESACTIVADO' ? '●' : '○')
                    : (item.adminStatus !== 'BLOQUEADO' ? '●' : '○')
                  }
                </span>
              </label>
            )
          })
        )}
      </div>
    </div>
  )
}

// ============================================
// Componente de configuración de Blocked Features
// Modelo: solo lo "incluido" se modifica. Lo no incluido queda intacto en cada entidad.
// ============================================
function BlockedFeaturesConfig({ blockedFeatures, onChange, included, onIncludedChange, features = BLOCKABLE_FEATURES }) {
  const toggleInclude = (featureId) => {
    if (included.has(featureId)) {
      const next = new Set(included)
      next.delete(featureId)
      onIncludedChange(next)
    } else {
      const next = new Set(included)
      next.add(featureId)
      onIncludedChange(next)
      const feature = features.find((f) => f.id === featureId)
      if (feature?.optIn) {
        if (!blockedFeatures.includes(featureId)) {
          onChange([...blockedFeatures, featureId])
        }
      } else if (!blockedFeatures.includes(featureId)) {
        onChange([...blockedFeatures, featureId])
      }
    }
  }

  const setAction = (featureId, activate) => {
    const feature = features.find((f) => f.id === featureId)
    const wantInList = feature?.optIn ? activate : !activate
    if (wantInList && !blockedFeatures.includes(featureId)) {
      onChange([...blockedFeatures, featureId])
    } else if (!wantInList && blockedFeatures.includes(featureId)) {
      onChange(blockedFeatures.filter((f) => f !== featureId))
    }
  }

  const includeAll = () => onIncludedChange(new Set(features.map(f => f.id)))
  const includeNone = () => onIncludedChange(new Set())

  return (
    <div className="bulk-config-section">
      <h4 className="bulk-config-title">Funcionalidades a Modificar</h4>
      <p className="bulk-config-desc" style={{ fontSize: '0.85rem' }}>
        <strong>Solo lo marcado se modificará.</strong> Las funcionalidades no incluidas mantendrán su estado actual en cada entidad.
      </p>

      <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.75rem' }}>
        <button className="text-btn" onClick={includeAll}>☑ Incluir todas</button>
        <button className="text-btn" onClick={includeNone}>☐ Excluir todas</button>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
        {features.map(feature => {
          const isIncluded = included.has(feature.id)
          const isGrant = feature.optIn === true
          const willBlock = isGrant
            ? !blockedFeatures.includes(feature.id)
            : blockedFeatures.includes(feature.id)
          return (
            <div
              key={feature.id}
              style={{
                border: '1px solid',
                borderColor: isIncluded ? (willBlock ? '#fecaca' : '#bbf7d0') : '#e5e7eb',
                background: isIncluded ? (willBlock ? '#fef2f2' : '#f0fdf4') : '#fff',
                borderRadius: '10px',
                padding: '0.75rem 0.9rem',
                opacity: isIncluded ? 1 : 0.65,
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', cursor: 'pointer', fontSize: '0.78rem', color: '#374151', flexShrink: 0 }}>
                  <input
                    type="checkbox"
                    checked={isIncluded}
                    onChange={() => toggleInclude(feature.id)}
                  />
                  Incluir
                </label>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 600, fontSize: '0.9rem', color: '#111827' }}>{feature.label}</div>
                  <div style={{ fontSize: '0.78rem', color: '#6b7280' }}>{feature.description}</div>
                </div>
                {isIncluded && (
                  <div style={{ display: 'flex', gap: '0.35rem', flexShrink: 0 }}>
                    <button
                      type="button"
                      onClick={() => setAction(feature.id, true)}
                      style={{
                        padding: '4px 10px',
                        fontSize: '0.75rem',
                        fontWeight: 600,
                        border: '1px solid',
                        borderColor: !willBlock ? '#10b981' : '#d1d5db',
                        background: !willBlock ? '#10b981' : '#fff',
                        color: !willBlock ? '#fff' : '#374151',
                        borderRadius: '6px',
                        cursor: 'pointer',
                      }}
                    >
                      Activar
                    </button>
                    <button
                      type="button"
                      onClick={() => setAction(feature.id, false)}
                      style={{
                        padding: '4px 10px',
                        fontSize: '0.75rem',
                        fontWeight: 600,
                        border: '1px solid',
                        borderColor: willBlock ? '#dc2626' : '#d1d5db',
                        background: willBlock ? '#dc2626' : '#fff',
                        color: willBlock ? '#fff' : '#374151',
                        borderRadius: '6px',
                        cursor: 'pointer',
                      }}
                    >
                      Bloquear
                    </button>
                  </div>
                )}
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

// ============================================
// Componente de configuración de Module Permissions
// Modelo: solo módulos "incluidos" se modifican. El resto queda intacto.
// ============================================
function ModulePermissionsConfig({ permissions, onChange, included, onIncludedChange, entityScope = 'users' }) {
  const moduleCategories = getModuleCategoriesForScope(entityScope)
  const [expandedCategories, setExpandedCategories] = useState({})

  const toggleExpanded = (categoryId) => {
    setExpandedCategories(prev => ({ ...prev, [categoryId]: !prev[categoryId] }))
  }

  const setIncluded = (ids, value) => {
    const next = new Set(included)
    ids.forEach(id => {
      if (value) next.add(id)
      else next.delete(id)
    })
    onIncludedChange(next)
  }

  const toggleIncludeOne = (id) => {
    const next = new Set(included)
    if (next.has(id)) next.delete(id)
    else next.add(id)
    onIncludedChange(next)
  }

  const setAccess = (ids, hasAccess) => {
    onChange(
      permissions.map(p => ids.includes(p.moduleId) ? { ...p, hasAccess } : p)
    )
    // Asegurar que estén incluidos si se les está fijando un valor
    setIncluded(ids, true)
  }

  const getAccess = (id) => {
    const p = permissions.find(pp => pp.moduleId === id)
    if (p && (p.hasAccess === true || p.hasAccess === false)) return p.hasAccess
    return resolveModuleHasAccess(p, id, true)
  }

  const includeAllInCategory = (catId, subIds) => {
    const ids = isRepositorioParentModuleId(catId) ? subIds : [catId, ...subIds]
    setIncluded(ids, true)
  }
  const excludeAllInCategory = (catId, subIds) => {
    const ids = isRepositorioParentModuleId(catId) ? subIds : [catId, ...subIds]
    setIncluded(ids, false)
  }

  const includeAll = () => onIncludedChange(new Set(getAllModuleIds(entityScope)))
  const includeNone = () => onIncludedChange(new Set())

  return (
    <div className="bulk-config-section">
      <h4 className="bulk-config-title">Módulos a Modificar</h4>
      <p className="bulk-config-desc" style={{ fontSize: '0.85rem' }}>
        <strong>Solo los módulos marcados se modificarán.</strong> Los demás mantendrán su configuración actual en cada entidad.
      </p>

      <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
        <button className="text-btn" onClick={includeAll}>☑ Incluir todos</button>
        <button className="text-btn" onClick={includeNone}>☐ Excluir todos</button>
      </div>

      <div className="folder-tree">
        {moduleCategories.map(category => {
          const subModuleIds = category.subModules.map(s => s.id)
          const hasSubModules = subModuleIds.length > 0
          const isExpanded = expandedCategories[category.id]
          const allIds = isRepositorioParentModuleId(category.id)
            ? subModuleIds
            : [category.id, ...subModuleIds]
          const includedCount = allIds.filter(id => included.has(id)).length
          const someIncluded = includedCount > 0
          const allIncluded = includedCount === allIds.length && allIds.length > 0
          const categoryHasAccess = isRepositorioParentModuleId(category.id)
            ? subModuleIds.some(id => getAccess(id))
            : getAccess(category.id)

          return (
            <div
              key={category.id}
              className="folder-item"
              style={{
                borderLeft: someIncluded ? '3px solid #3b82f6' : '3px solid transparent',
              }}
            >
              <div
                className="folder-header"
                style={{
                  background: someIncluded ? '#eff6ff' : undefined,
                  opacity: someIncluded ? 1 : 0.7,
                }}
              >
                <label
                  title={allIncluded ? 'Excluir esta categoría' : 'Incluir esta categoría'}
                  style={{ display: 'flex', alignItems: 'center', gap: '4px', cursor: 'pointer', marginRight: '4px' }}
                >
                  <input
                    type="checkbox"
                    checked={allIncluded}
                    ref={el => { if (el) el.indeterminate = someIncluded && !allIncluded }}
                    onChange={() => allIncluded ? excludeAllInCategory(category.id, subModuleIds) : includeAllInCategory(category.id, subModuleIds)}
                  />
                </label>
                {hasSubModules ? (
                  <button className="folder-expand-btn" onClick={() => toggleExpanded(category.id)}>
                    {isExpanded ? '▼' : '▶'}
                  </button>
                ) : (
                  <span className="folder-expand-placeholder"></span>
                )}
                <span className="folder-name" onClick={() => hasSubModules && toggleExpanded(category.id)}>
                  {category.name}
                </span>
                {someIncluded && !allIncluded && (
                  <span style={{ fontSize: '0.7rem', background: '#dbeafe', color: '#1e40af', padding: '1px 8px', borderRadius: '999px' }}>
                    {includedCount}/{allIds.length} incluidos
                  </span>
                )}
                {someIncluded && (
                  <div style={{ display: 'flex', gap: '0.3rem', marginLeft: 'auto' }}>
                    <button
                      type="button"
                      onClick={() => setAccess(allIds, true)}
                      style={{
                        padding: '3px 9px', fontSize: '0.72rem', fontWeight: 600, borderRadius: '6px',
                        border: '1px solid', borderColor: categoryHasAccess ? '#10b981' : '#d1d5db',
                        background: categoryHasAccess ? '#10b981' : '#fff',
                        color: categoryHasAccess ? '#fff' : '#374151', cursor: 'pointer',
                      }}
                    >Activar</button>
                    <button
                      type="button"
                      onClick={() => setAccess(allIds, false)}
                      style={{
                        padding: '3px 9px', fontSize: '0.72rem', fontWeight: 600, borderRadius: '6px',
                        border: '1px solid', borderColor: !categoryHasAccess ? '#dc2626' : '#d1d5db',
                        background: !categoryHasAccess ? '#dc2626' : '#fff',
                        color: !categoryHasAccess ? '#fff' : '#374151', cursor: 'pointer',
                      }}
                    >Bloquear</button>
                  </div>
                )}
              </div>

              {hasSubModules && isExpanded && (
                <div className="folder-children">
                  {category.subModules.map(subMod => {
                    const subIncluded = included.has(subMod.id)
                    const subAccess = getAccess(subMod.id)
                    return (
                      <div
                        key={subMod.id}
                        className="folder-child"
                        style={{
                          opacity: subIncluded ? 1 : 0.55,
                          background: subIncluded ? '#f0f9ff' : undefined,
                        }}
                      >
                        <label style={{ display: 'flex', alignItems: 'center', gap: '4px', cursor: 'pointer', marginRight: '4px' }}>
                          <input
                            type="checkbox"
                            checked={subIncluded}
                            onChange={() => toggleIncludeOne(subMod.id)}
                          />
                        </label>
                        <span className="child-name">{subMod.name}</span>
                        {subIncluded && (
                          <div style={{ display: 'flex', gap: '0.25rem', marginLeft: 'auto' }}>
                            <button
                              type="button"
                              onClick={() => setAccess([subMod.id], true)}
                              style={{
                                padding: '2px 8px', fontSize: '0.7rem', fontWeight: 600, borderRadius: '5px',
                                border: '1px solid', borderColor: subAccess ? '#10b981' : '#d1d5db',
                                background: subAccess ? '#10b981' : '#fff',
                                color: subAccess ? '#fff' : '#374151', cursor: 'pointer',
                              }}
                            >Activar</button>
                            <button
                              type="button"
                              onClick={() => setAccess([subMod.id], false)}
                              style={{
                                padding: '2px 8px', fontSize: '0.7rem', fontWeight: 600, borderRadius: '5px',
                                border: '1px solid', borderColor: !subAccess ? '#dc2626' : '#d1d5db',
                                background: !subAccess ? '#dc2626' : '#fff',
                                color: !subAccess ? '#fff' : '#374151', cursor: 'pointer',
                              }}
                            >Bloquear</button>
                          </div>
                        )}
                      </div>
                    )
                  })}
                </div>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}

// ============================================
// Página principal de Permisos Masivos
// ============================================
export function BulkPermissions() {
  // Tabs: 'users' | 'companies'
  const [entityTab, setEntityTab] = useState('users')
  // Tipo de permiso: 'blocked' | 'modules'
  const [permissionType, setPermissionType] = useState('blocked')
  // Entidades seleccionadas
  const [selectedIds, setSelectedIds] = useState([])
  // Todos los items cargados (para detectar discrepancias)
  const [allItems, setAllItems] = useState([])
  // Configuración de permisos
  const [blockedFeatures, setBlockedFeatures] = useState([])
  const buildDefaultModulePermissions = (entityTab) =>
    getAllModuleIds(entityTab).map((id) => ({
      moduleId: id,
      hasAccess:
        id === FICHA_CLIENTE_MODULE_ID && entityTab === 'users'
          ? false
          : resolveModuleHasAccess(undefined, id, true),
      excludedFeatures: [],
    }))

  const [modulePermissions, setModulePermissions] = useState(() => buildDefaultModulePermissions('users'))
  // Sets de "qué incluir" en el bulk update (sólo lo incluido se modifica en el backend)
  const [includedFeatures, setIncludedFeatures] = useState(() => new Set())
  const [includedModules, setIncludedModules] = useState(() => new Set())
  // Estado de resultado
  const [resultMessage, setResultMessage] = useState(null)
  const [loading, setLoading] = useState(false)
  // Modal de discrepancias
  const [discrepancyModal, setDiscrepancyModal] = useState(null)
  // Incrementar tras aplicar OK para que EntitySelector refetch con network-only (caché no refleja permisos nuevos).
  const [entitiesListEpoch, setEntitiesListEpoch] = useState(0)

  // Mutations
  const [, bulkUpdateUserBlocked] = useMutation(BULK_UPDATE_USER_BLOCKED_FEATURES)
  const [, bulkUpdateUserPerms] = useMutation(BULK_UPDATE_USER_PERMISSIONS)
  const [, bulkUpdateCompanyBlocked] = useMutation(BULK_UPDATE_COMPANY_BLOCKED_FEATURES)
  const [, bulkUpdateCompanyPerms] = useMutation(BULK_UPDATE_COMPANY_PERMISSIONS)

  // Reset cuando se cambia de tab o tipo
  useEffect(() => {
    setSelectedIds([])
    setResultMessage(null)
    setAllItems([])
    setModulePermissions(buildDefaultModulePermissions(entityTab))
    setIncludedModules(new Set())
  }, [entityTab])

  useEffect(() => {
    setResultMessage(null)
  }, [permissionType])

  const resetFormSelectionsOnly = () => {
    setSelectedIds([])
    setBlockedFeatures([])
    setModulePermissions(buildDefaultModulePermissions(entityTab))
    setIncludedFeatures(new Set())
    setIncludedModules(new Set())
    setDiscrepancyModal(null)
  }

  const resetAll = () => {
    resetFormSelectionsOnly()
    setResultMessage(null)
  }

  const onApplySuccess = (message) => {
    resetFormSelectionsOnly()
    setEntitiesListEpoch(e => e + 1)
    setResultMessage({ type: 'success', text: message })
  }

  // Construye los inputs efectivos: sólo lo incluido
  const effectiveBlocked = useMemo(
    () => blockedFeatures.filter(f => includedFeatures.has(f)),
    [blockedFeatures, includedFeatures]
  )
  const effectiveUnblock = useMemo(
    () => Array.from(includedFeatures).filter(f => !blockedFeatures.includes(f)),
    [blockedFeatures, includedFeatures]
  )
  const effectiveModulePerms = useMemo(
    () => modulePermissions.filter(p => includedModules.has(p.moduleId)),
    [modulePermissions, includedModules]
  )

  // Ejecuta la mutación real con los IDs ya resueltos (siempre en modo merge)
  const executeApply = async (idsToApply) => {
    setLoading(true)
    setResultMessage(null)
    setDiscrepancyModal(null)

    try {
      let result

      if (entityTab === 'users' && permissionType === 'blocked') {
        result = await bulkUpdateUserBlocked({
          input: {
            userIds: idsToApply,
            blockedFeatures: effectiveBlocked,
            featuresToUnblock: effectiveUnblock,
            merge: true,
          },
        })
        if (result.error) throw result.error
        onApplySuccess(result.data.bulkUpdateUserBlockedFeatures.message)
      }

      if (entityTab === 'users' && permissionType === 'modules') {
        result = await bulkUpdateUserPerms({
          input: {
            userIds: idsToApply,
            modulePermissions: effectiveModulePerms.map(p => ({
              moduleId: p.moduleId,
              hasAccess: p.hasAccess,
              excludedFeatures: p.excludedFeatures || [],
            })),
            merge: true,
          },
        })
        if (result.error) throw result.error
        onApplySuccess(result.data.bulkUpdateUserPermissions.message)
      }

      if (entityTab === 'companies' && permissionType === 'blocked') {
        const userOnlyIds = new Set(BLOCKABLE_FEATURES.filter((f) => f.userOnly).map((f) => f.id))
        result = await bulkUpdateCompanyBlocked({
          input: {
            companyIds: idsToApply,
            blockedFeatures: effectiveBlocked.filter((id) => !userOnlyIds.has(id)),
            featuresToUnblock: effectiveUnblock.filter((id) => !userOnlyIds.has(id)),
            merge: true,
          },
        })
        if (result.error) throw result.error
        onApplySuccess(result.data.bulkUpdateCompanyBlockedFeatures.message)
      }

      if (entityTab === 'companies' && permissionType === 'modules') {
        result = await bulkUpdateCompanyPerms({
          input: {
            companyIds: idsToApply,
            modulePermissions: effectiveModulePerms.map(p => ({
              moduleId: p.moduleId,
              hasAccess: p.hasAccess,
              excludedFeatures: p.excludedFeatures || [],
            })),
            merge: true,
          },
        })
        if (result.error) throw result.error
        onApplySuccess(result.data.bulkUpdateCompanyPermissions.message)
      }
    } catch (err) {
      setResultMessage({
        type: 'error',
        text: 'Error: ' + (err?.message || err?.graphQLErrors?.[0]?.message || 'Error desconocido'),
      })
    } finally {
      setLoading(false)
    }
  }

  const handleApply = async () => {
    if (selectedIds.length === 0) return

    // Validar que haya algo incluido para modificar
    if (permissionType === 'blocked' && includedFeatures.size === 0) {
      setResultMessage({ type: 'error', text: 'Marca al menos una funcionalidad como "Incluir" para aplicar el cambio.' })
      return
    }
    if (permissionType === 'modules' && includedModules.size === 0) {
      setResultMessage({ type: 'error', text: 'Marca al menos un módulo como "Incluir" para aplicar el cambio.' })
      return
    }

    // Detectar discrepancias contra la configuración EFECTIVA (solo lo incluido)
    const selectedEntities = allItems.filter(item => selectedIds.includes(item._id))
    const discrepancies = detectDiscrepancies(selectedEntities, permissionType, effectiveBlocked, effectiveModulePerms)

    if (discrepancies.length > 0) {
      setDiscrepancyModal({ discrepancies, idsToApply: selectedIds })
      return
    }

    await executeApply(selectedIds)
  }

  // Conteos usando solo lo "incluido"
  const includedFeaturesCount = includedFeatures.size
  const includedModulesCount = includedModules.size
  const featuresToBlockCount = effectiveBlocked.length
  const featuresToActivateCount = effectiveUnblock.length
  const modulesToBlockCount = effectiveModulePerms.filter(p => !p.hasAccess).length
  const modulesToActivateCount = effectiveModulePerms.filter(p => p.hasAccess).length

  // Conteo de conflictos en la selección actual (solo considerando lo incluido)
  const conflictCountInSelection = useMemo(() => {
    if (selectedIds.length === 0 || allItems.length === 0) return 0
    const selectedEntities = allItems.filter(it => selectedIds.includes(it._id))
    return detectDiscrepancies(selectedEntities, permissionType, effectiveBlocked, effectiveModulePerms).length
  }, [selectedIds, allItems, permissionType, effectiveBlocked, effectiveModulePerms])

  return (
    <>
      {/* Modal de discrepancias */}
      {discrepancyModal && (
        <DiscrepancyModal
          isOpen={true}
          onClose={() => setDiscrepancyModal(null)}
          onApplyAll={() => executeApply(discrepancyModal.idsToApply)}
          onApplyConflictingOnly={() => {
            const ids = discrepancyModal.discrepancies.map(d => d.entity._id)
            executeApply(ids)
          }}
          discrepancies={discrepancyModal.discrepancies}
          entityType={entityTab}
          totalSelected={discrepancyModal.idsToApply.length}
        />
      )}

      {/* Header */}
      <div className="detail-header">
        <div className="detail-title-section">
          <div>
            <h1 className="page-title">Permisos Masivos</h1>
            <p className="page-subtitle">
              Actualiza permisos de múltiples {entityTab === 'users' ? 'usuarios' : 'empresas'} a la vez
            </p>
          </div>
        </div>
      </div>

      {/* Selector de entidad (Users / Companies) */}
      <div className="content-card" style={{ marginBottom: '1.5rem' }}>
        <div className="card-body" style={{ padding: '0.75rem 1rem' }}>
          <div className="mode-tabs">
            <button
              className={`mode-tab ${entityTab === 'users' ? 'active' : ''}`}
              onClick={() => setEntityTab('users')}
            >
              Usuarios
            </button>
            <button
              className={`mode-tab ${entityTab === 'companies' ? 'active' : ''}`}
              onClick={() => setEntityTab('companies')}
            >
              Empresas
            </button>
          </div>
        </div>
      </div>

      {/* Selector de tipo de permiso */}
      <div className="content-card" style={{ marginBottom: '1.5rem' }}>
        <div className="card-body" style={{ padding: '0.75rem 1rem' }}>
          <div className="mode-tabs">
            <button
              className={`mode-tab ${permissionType === 'blocked' ? 'active' : ''}`}
              onClick={() => setPermissionType('blocked')}
            >
              Funcionalidades Bloqueadas
            </button>
            <button
              className={`mode-tab ${permissionType === 'modules' ? 'active' : ''}`}
              onClick={() => setPermissionType('modules')}
            >
              Permisos de Módulos
            </button>
          </div>
        </div>
      </div>

      {/* Resultado */}
      {resultMessage && (
        <div className={`alert ${resultMessage.type === 'success' ? 'success' : 'error'}`} style={{ marginBottom: '1.5rem' }}>
          {resultMessage.text}
        </div>
      )}

      {/* Contenido principal en 2 columnas */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem', marginBottom: '1.5rem' }}>
        {/* Columna izquierda: selección de entidades */}
        <div className="content-card">
          <div className="card-header">
            <h3 className="card-title">
              Seleccionar {entityTab === 'users' ? 'Usuarios' : 'Empresas'}
            </h3>
          </div>
          <div className="card-body" style={{ maxHeight: '500px', overflowY: 'auto' }}>
            <EntitySelector
              entityType={entityTab}
              selected={selectedIds}
              onSelectionChange={setSelectedIds}
              onItemsLoaded={setAllItems}
              permissionType={permissionType}
              blockedFeatures={effectiveBlocked}
              modulePermissions={effectiveModulePerms}
              listRefetchEpoch={entitiesListEpoch}
            />
          </div>
        </div>

        {/* Columna derecha: configuración de permisos */}
        <div className="content-card">
          <div className="card-header">
            <h3 className="card-title">
              Configurar Permisos
            </h3>
          </div>
          <div className="card-body" style={{ maxHeight: '500px', overflowY: 'auto' }}>
            {permissionType === 'blocked' ? (
              <BlockedFeaturesConfig
                blockedFeatures={blockedFeatures}
                onChange={setBlockedFeatures}
                included={includedFeatures}
                onIncludedChange={setIncludedFeatures}
                features={
                  entityTab === 'users'
                    ? BLOCKABLE_FEATURES
                    : BLOCKABLE_FEATURES.filter((feature) => !feature.userOnly)
                }
              />
            ) : (
              <ModulePermissionsConfig
                entityScope={entityTab}
                permissions={modulePermissions}
                onChange={setModulePermissions}
                included={includedModules}
                onIncludedChange={setIncludedModules}
              />
            )}
          </div>
        </div>
      </div>

      {/* Resumen y botón de aplicar */}
      <div className="content-card">
        <div className="card-body">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem' }}>
            <div>
              <div style={{ display: 'flex', gap: '1.5rem', flexWrap: 'wrap' }}>
                <div>
                  <span style={{ fontWeight: 600, color: '#374151' }}>
                    {selectedIds.length}
                  </span>
                  <span style={{ color: '#6b7280', marginLeft: '0.25rem' }}>
                    {entityTab === 'users' ? 'usuario' : 'empresa'}{selectedIds.length !== 1 ? 's' : ''} seleccionado{selectedIds.length !== 1 ? 's' : ''}
                  </span>
                </div>
                <div>
                  {permissionType === 'blocked' ? (
                    <span style={{ color: '#6b7280' }}>
                      <span style={{ fontWeight: 600, color: includedFeaturesCount > 0 ? '#1d4ed8' : '#9ca3af' }}>
                        {includedFeaturesCount}
                      </span>{' '}
                      funcionalidad{includedFeaturesCount !== 1 ? 'es' : ''} a modificar
                      {includedFeaturesCount > 0 && (
                        <> ({featuresToBlockCount} a bloquear, {featuresToActivateCount} a activar)</>
                      )}
                    </span>
                  ) : (
                    <span style={{ color: '#6b7280' }}>
                      <span style={{ fontWeight: 600, color: includedModulesCount > 0 ? '#1d4ed8' : '#9ca3af' }}>
                        {includedModulesCount}
                      </span>{' '}
                      módulo{includedModulesCount !== 1 ? 's' : ''} a modificar
                      {includedModulesCount > 0 && (
                        <> ({modulesToBlockCount} a bloquear, {modulesToActivateCount} a activar)</>
                      )}
                    </span>
                  )}
                </div>
              </div>
              {selectedIds.length === 0 && (
                <p className="bulk-selection-caption">
                  Selecciona al menos {entityTab === 'users' ? 'un usuario' : 'una empresa'} para aplicar los cambios.
                </p>
              )}
            </div>
            <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
              <button className="action-btn secondary" onClick={resetAll}>
                Limpiar Todo
              </button>
              <button
                className="action-btn primary"
                disabled={loading || selectedIds.length === 0}
                onClick={handleApply}
                title={
                  selectedIds.length === 0
                    ? `Selecciona al menos ${entityTab === 'users' ? 'un usuario' : 'una empresa'} para aplicar los cambios.`
                    : undefined
                }
              >
                {loading
                  ? 'Aplicando...'
                  : `Aplicar a ${selectedIds.length} ${entityTab === 'users' ? 'usuario' : 'empresa'}${selectedIds.length !== 1 ? 's' : ''}`
                }
              </button>
            </div>
          </div>

          {selectedIds.length > 0 && conflictCountInSelection > 0 && (
            <div style={{ marginTop: '0.75rem', padding: '0.75rem 0.9rem', backgroundColor: '#fef2f2', borderRadius: '8px', border: '1px solid #fecaca', fontSize: '0.85rem', color: '#991b1b', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <span>⚠️</span>
              <span>
                <strong>{conflictCountInSelection}</strong> de {selectedIds.length} {entityTab === 'users' ? 'usuario' : 'empresa'}{selectedIds.length !== 1 ? 's' : ''} tendrán cambios respecto a su configuración actual. Al aplicar verás el detalle y podrás decidir.
              </span>
            </div>
          )}
        </div>
      </div>
    </>
  )
}

export default BulkPermissions
